/**
 * Typo-tolerant, offline matching for small, already-fetched lists (today:
 * only the authority pickers — QRReport/AuthorityPicker and
 * ForwardReportModal). Everything here runs client-side against a list the
 * component already has in memory: no network call, no server cost. A list
 * that tops out at a few hundred rows doesn't justify Elasticsearch or
 * embeddings — this covers the same typo/partial-word cases for free.
 *
 * Deliberately NOT a general full-text search: it has no notion of relevance
 * across documents, no stemming, no language model. It answers one narrow
 * question per candidate — "did the admin's name/keywords probably mean to
 * contain this query?" — tolerating one or two typed/missing letters.
 */

/** Matches BReporter-Backend's own ApiService.MIN_SEARCH_LENGTH reasoning: a
 *  1-2 char query against free text is mostly noise (near-everything matches
 *  "a" or "an"), and fuzzy distance on a 1-2 char string has no useful
 *  meaning either ("ab" is one edit away from a huge fraction of 3-letter
 *  tokens). Below this length we fall back to a plain, cheap substring test. */
const MIN_FUZZY_LENGTH = 3;

/** Levenshtein edit distance, capped: as soon as every remaining way to
 *  finish costs more than `max`, bail out and return `max + 1`. The
 *  candidate lists here are at most a few hundred short tokens, so the
 *  uncapped O(a.length * b.length) cost is negligible either way — the cap
 *  mainly keeps this correct-by-construction as a "distance or too far"
 *  primitive rather than a tight hot-path optimization. */
function levenshtein(a: string, b: string, max: number): number {
	if (Math.abs(a.length - b.length) > max) return max + 1;
	if (a.length === 0) return Math.min(b.length, max + 1);
	if (b.length === 0) return Math.min(a.length, max + 1);

	let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
	for (let i = 1; i <= a.length; i++) {
		const curr = [i];
		let rowMin = curr[0];
		for (let j = 1; j <= b.length; j++) {
			const cost = a[i - 1] === b[j - 1] ? 0 : 1;
			curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
			rowMin = Math.min(rowMin, curr[j]);
		}
		// Every cell in this row is already past budget — no way to recover.
		if (rowMin > max) return max + 1;
		prev = curr;
	}
	return Math.min(prev[b.length], max + 1);
}

/** Query length determines typo tolerance: more letters, more room for a
 *  slip without the match becoming meaningless. A 3-4 letter query allows 1
 *  edit ("tab" ~ "tap"); 5+ allows 2 ("caretkr" ~ "caretaker" is 2 edits). */
function typoBudget(queryLength: number): number {
	return queryLength <= 4 ? 1 : 2;
}

function normalize(s: string): string {
	// NFC so visually-identical Devanagari/other composed-vs-decomposed
	// sequences compare equal; plain lowercasing alone only handles ASCII.
	return s.normalize('NFC').trim().toLowerCase();
}

/**
 * Score a single piece of text (an authority name, or one keyword) against a
 * query. Higher is a better match; 0 means no match at all. Exact substring
 * matches always outrank fuzzy ones, and an earlier match position outranks
 * a later one (prefix matches sort to the top).
 */
function scoreText(query: string, text: string): number {
	if (!query) return 0;
	const idx = text.indexOf(query);
	if (idx !== -1) {
		// 1000 base for "found it at all", minus position so earlier (and
		// especially prefix, idx === 0) matches sort first.
		return 1000 - idx;
	}
	if (query.length < MIN_FUZZY_LENGTH) return 0;

	const budget = typoBudget(query.length);
	let best = 0;
	for (const token of text.split(/\s+/)) {
		if (!token) continue;
		const distance = levenshtein(query, token, budget);
		if (distance <= budget) {
			// Fuzzy matches always rank below every substring match (capped
			// under 1000) and closer edits rank above farther ones.
			best = Math.max(best, 500 - distance * 100);
		}
	}
	return best;
}

/**
 * Score one authority's name + comma-separated keywords against a query.
 * `keywords` is the free-text admin field (Authority.keywords, e.g.
 * "plumber, tap, leak, water, washroom") — see
 * BReporter-Backend/scripts/add-authority-keywords.sql. A name match always
 * outranks a keywords-only match (users searching by the name they typed
 * should see that authority above one that merely shares a symptom word).
 */
export function scoreAuthority(query: string, name: string, keywords?: string): number {
	const q = normalize(query);
	if (!q) return 1; // empty query: keep original order, don't hide anything

	const nameScore = scoreText(q, normalize(name));
	if (nameScore > 0) return nameScore;

	let keywordScore = 0;
	for (const kw of (keywords ?? '').split(',')) {
		const trimmed = normalize(kw);
		if (!trimmed) continue;
		keywordScore = Math.max(keywordScore, scoreText(q, trimmed));
	}
	// Halved and offset below the lowest possible name score (1) can't reach,
	// so keywords never outrank even a weak name match — they only add
	// results a pure name search would have missed entirely.
	return keywordScore > 0 ? keywordScore / 2 : 0;
}

/**
 * Filter + sort a list of authorities against a query, best match first.
 * `getName`/`getKeywords` let callers pass their own authority shape (the QR
 * picker and the forward modal each have a slightly different interface)
 * without this module needing to know either one.
 */
export function filterAuthorities<T>(
	items: T[],
	query: string,
	getName: (item: T) => string,
	getKeywords?: (item: T) => string | undefined
): T[] {
	const q = query.trim();
	if (!q) return items;

	return items
		.map((item) => ({ item, score: scoreAuthority(q, getName(item), getKeywords?.(item)) }))
		.filter((x) => x.score > 0)
		.sort((a, b) => b.score - a.score)
		.map((x) => x.item);
}
